import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaTimes, FaExternalLinkAlt } from 'react-icons/fa';
import cmsApi from '../../services/cmsApi';
import { getImageUrl } from '../../utils/getImageUrl';
import ExeedLogo from '../../assets/Exeed ECX.svg';

const GOLD = '#fca903';
const DISMISSED_KEY = 'jriit_popup_dismissed';

const FALLBACK_AD = {
  id: 'fallback-ecx',
  title: 'Discover ECX',
  subtitle: 'Engage • Educate • Empower',
  ctaText: 'Visit myexeed.com',
  logoUrl: null,
  url: 'http://www.myexeed.com/',
  showDelayMs: 1500,
};

function adId(id) {
  return String(id);
}

function getDismissedIds() {
  try {
    const raw = sessionStorage.getItem(DISMISSED_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map(adId) : [];
  } catch {
    return [];
  }
}

function dismissAd(id) {
  const key = adId(id);
  const dismissed = getDismissedIds();
  if (!dismissed.includes(key)) {
    dismissed.push(key);
    sessionStorage.setItem(DISMISSED_KEY, JSON.stringify(dismissed));
  }
}

function pickNextAd(ads) {
  const dismissed = getDismissedIds();
  return ads.find(ad => !dismissed.includes(adId(ad.id))) ?? null;
}

const FloatingDegreeIcon = () => {
  const [ads, setAds] = useState([]);
  const [currentAd, setCurrentAd] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const showTimerRef = useRef(null);
  const finishedRef = useRef(false);

  const clearShowTimer = () => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
  };

  /** Wait showDelayMs, then display the given ad. */
  const scheduleShow = useCallback((ad) => {
    if (!ad || finishedRef.current) return;
    clearShowTimer();
    const delay = Math.max(0, ad.showDelayMs ?? 1500);
    showTimerRef.current = setTimeout(() => {
      if (finishedRef.current) return;
      setCurrentAd(ad);
      setIsVisible(true);
      setIsClosing(false);
    }, delay);
  }, []);

  useEffect(() => {
    let cancelled = false;

    cmsApi.getPopupAds().then(res => {
      if (cancelled || finishedRef.current) return;
      const list = res.success && res.announcements?.length
        ? res.announcements
        : [FALLBACK_AD];
      setAds(list);
      const first = pickNextAd(list);
      if (first) scheduleShow(first);
    });

    return () => {
      cancelled = true;
      clearShowTimer();
    };
  }, [scheduleShow]);

  const handleClose = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (!currentAd || finishedRef.current) return;

    dismissAd(currentAd.id);
    clearShowTimer();
    setIsClosing(true);

    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);

      const next = pickNextAd(ads);
      if (next) {
        // showDelayMs on the next ad = wait after previous ad was closed
        scheduleShow(next);
      } else {
        setCurrentAd(null);
        finishedRef.current = true;
      }
    }, 280);
  };

  const handleVisit = (e) => {
    e.stopPropagation();
    const url = currentAd?.url;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    handleClose();
  };

  if (!currentAd || !isVisible) return null;

  const logoSrc = currentAd.logoUrl
    ? getImageUrl(currentAd.logoUrl)
    : ExeedLogo;

  return (
    <>
      <div
        className={isClosing ? 'ecx-overlay-out' : 'ecx-overlay-in'}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem',
          backgroundColor: 'rgba(0,0,0,0.65)',
          backdropFilter: 'blur(2px)',
        }}
        onClick={handleClose}
      >
        <div
          className={isClosing ? 'ecx-modal-out' : 'ecx-modal-in'}
          style={{
            position: 'relative',
            background: '#ffffff',
            borderRadius: '16px',
            overflow: 'hidden',
            width: '480px',
            maxWidth: '100%',
            boxShadow: '0 32px 80px rgba(0,0,0,0.35), 0 0 0 1px rgba(0,0,0,0.08)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ height: '4px', background: `linear-gradient(90deg, #e09200, ${GOLD}, #e09200)` }} />

          <div style={{ padding: '2.75rem 3rem 2.5rem', textAlign: 'center' }}>
            {logoSrc && (
              <img
                src={logoSrc}
                alt={currentAd.title || 'Advertisement'}
                style={{ height: '68px', width: 'auto', margin: '0 auto 2rem', display: 'block' }}
              />
            )}

            <h2 style={{ fontSize: '2.1rem', fontWeight: 800, color: '#0d1117', lineHeight: 1.2, marginBottom: '0.6rem' }}>
              {currentAd.title}
            </h2>

            {currentAd.subtitle && (
              <p style={{ fontSize: '1.05rem', fontWeight: 600, color: GOLD, letterSpacing: '0.03em', marginBottom: '2.25rem' }}>
                {currentAd.subtitle}
              </p>
            )}

            <button
              onClick={handleVisit}
              className="ecx-btn"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.875rem 2.25rem',
                borderRadius: '9999px',
                border: 'none',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: '0.9rem',
                background: `linear-gradient(135deg, ${GOLD}, #e09200)`,
                color: '#0d1117',
                boxShadow: `0 4px 20px ${GOLD}55`,
                transition: 'transform 0.2s, box-shadow 0.2s',
                marginTop: currentAd.subtitle ? 0 : '1.5rem',
              }}
            >
              {currentAd.ctaText || 'Learn More'}
              <FaExternalLinkAlt size={11} />
            </button>
          </div>

          <div style={{ height: '4px', background: `linear-gradient(90deg, #e09200, ${GOLD}, #e09200)` }} />

          <button
            onClick={handleClose}
            className="ecx-close"
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#f3f4f6',
              color: '#6b7280',
              transition: 'background 0.15s',
            }}
            aria-label="Close"
          >
            <FaTimes size={12} />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes ecxOverlayIn  { from { opacity: 0 } to { opacity: 1 } }
        @keyframes ecxOverlayOut { from { opacity: 1 } to { opacity: 0 } }
        @keyframes ecxModalIn {
          0%   { transform: scale(0.5)  translateY(24px); opacity: 0; }
          65%  { transform: scale(1.04) translateY(-3px); opacity: 1; }
          100% { transform: scale(1)    translateY(0);    opacity: 1; }
        }
        @keyframes ecxModalOut {
          from { transform: scale(1);   opacity: 1; }
          to   { transform: scale(0.85); opacity: 0; }
        }
        .ecx-overlay-in  { animation: ecxOverlayIn  0.3s ease forwards; }
        .ecx-overlay-out { animation: ecxOverlayOut 0.28s ease forwards; }
        .ecx-modal-in    { animation: ecxModalIn  0.45s cubic-bezier(0.34, 1.56, 0.64, 1) forwards; }
        .ecx-modal-out   { animation: ecxModalOut 0.25s ease-in forwards; }
        .ecx-btn:hover   { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(252,169,3,0.45) !important; }
        .ecx-close:hover { background: #e5e7eb !important; }
      `}</style>
    </>
  );
};

export default FloatingDegreeIcon;
